import { toast } from "@/components/ui/use-toast";
import {
  useRetryAllFailedMutation,
  useRetryFailedStoreMutation,
} from "@/services/campaign-propagation";
import { CampaignType } from "@/utils/campaign-types";

interface UsePropagationRetryParams {
  type: CampaignType;
  code: number;
  onSuccess?: () => void;
}

interface UsePropagationRetryReturn {
  retryAll: () => Promise<void>;
  retryStore: (storeId: number) => Promise<void>;
  isRetryingAll: boolean;
  isRetryingStore: boolean;
}

/**
 * Hook customizado para gerenciar retry de propagações
 * Centraliza lógica de retry com tratamento de erros e feedback
 */
export function usePropagationRetry({
  type,
  code,
  onSuccess,
}: UsePropagationRetryParams): UsePropagationRetryReturn {
  const [retryAllMutation, { isLoading: isRetryingAll }] = useRetryAllFailedMutation();
  const [retryStoreMutation, { isLoading: isRetryingStore }] = useRetryFailedStoreMutation();

  const retryAll = async () => {
    try {
      const result = await retryAllMutation({ type, code }).unwrap();

      toast({
        variant: "default",
        description: result.message || "Lojas reprocessadas com sucesso",
        duration: 5000,
      });

      onSuccess?.();
    } catch (error: any) {
      toast({
        variant: "destructive",
        description: error?.data?.message || "Erro ao reprocessar lojas",
        duration: 5000,
      });
    }
  };

  const retryStore = async (storeId: number) => {
    try {
      await retryStoreMutation(storeId).unwrap();

      toast({
        variant: "default",
        description: "Loja reprocessada com sucesso",
        duration: 5000,
      });

      onSuccess?.();
    } catch (error: any) {
      toast({
        variant: "destructive",
        description: error?.data?.message || "Erro ao reprocessar loja",
        duration: 5000,
      });
    }
  };

  return {
    retryAll,
    retryStore,
    isRetryingAll,
    isRetryingStore,
  };
}
